import React from "react";
import CandidatePageShell from "./CandidatePageShell";
import useFetch from "../../useFetch";
import { getStoredUser } from "../../auth";
import AccountReferralCard from "../Common/AccountReferralCard";
import { getSafeExternalUrl } from "../../utils/externalUrls";
import "./Candidates.css";

const getInitials = (name) => {
  const words = String(name || "C")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words.slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase() || "C";
};

const splitSkills = (skills) => {
  if (Array.isArray(skills)) return skills;
  if (!skills) return [];

  return String(skills).split(",").map((skill) => skill.trim()).filter(Boolean);
};

const CandidateViewProfile = () => {
  const storedUser = getStoredUser();
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + "/candidate-profile");
  const profile = response?.candidate_profile;
  const account = response?.account ?? storedUser;
  const displayName = profile?.full_name ?? storedUser?.name ?? "Candidate";
  const displayEmail = profile?.email ?? storedUser?.email;
  const skills = splitSkills(profile?.skills);
  const safeWebsite = getSafeExternalUrl(profile?.website);
  const safeLinkedinUrl = getSafeExternalUrl(profile?.linkedin_url);

  return (
    <CandidatePageShell
      active="view-profile"
      title="View Profile"
      text="Preview the basic candidate profile employers can review."
    >
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}
      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {!isLoading && !error && (
        <div className="ls-widget candidate-account-widget">
          <div className="widget-title">
            <h4>Account Referral</h4>
          </div>
          <div className="widget-content">
            <AccountReferralCard account={account} />
          </div>
        </div>
      )}

      {!isLoading && !error && !profile && (
        <div className="employee-empty-state">
          No candidate profile found. Add your profile details first.
        </div>
      )}

      {!isLoading && !error && profile && (
        <div className="row">
          <div className="col-lg-4 col-md-12">
            <div className="ls-widget candidate-profile-card">
              <div className="widget-content">
                <div className="candidate-profile-avatar">
                  {profile.photo_url ? (
                    <img src={profile.photo_url} alt={displayName} />
                  ) : (
                    <span>{getInitials(displayName)}</span>
                  )}
                </div>
                <h4>{displayName}</h4>
                <p>{profile.professional_title}</p>
                <ul>
                  {profile.location && <li><span className="la la-map-marker"></span>{profile.location}</li>}
                  {displayEmail && <li><span className="la la-envelope"></span>{displayEmail}</li>}
                  {profile.phone && <li><span className="la la-phone"></span>{profile.phone}</li>}
                  {profile.experience && <li><span className="la la-briefcase"></span>{profile.experience}</li>}
                  {profile.expected_salary && <li><span className="la la-money"></span>{profile.expected_salary}</li>}
                </ul>
              </div>
            </div>
          </div>

          <div className="col-lg-8 col-md-12">
            <div className="ls-widget candidate-profile-summary">
              <div className="widget-title">
                <h4>Profile Summary</h4>
              </div>
              <div className="widget-content">
                {profile.bio && <p>{profile.bio}</p>}

                {skills.length > 0 && (
                  <>
                    <h5>Skills</h5>
                    <div className="candidate-skill-tags">
                      {skills.map((skill) => (
                        <span key={skill}>{skill}</span>
                      ))}
                    </div>
                  </>
                )}

                {(safeWebsite || safeLinkedinUrl) && (
                  <>
                    <h5>Links</h5>
                    <div className="candidate-profile-links">
                      {safeWebsite && <a href={safeWebsite} target="_blank" rel="noreferrer">Portfolio</a>}
                      {safeLinkedinUrl && <a href={safeLinkedinUrl} target="_blank" rel="noreferrer">LinkedIn</a>}
                    </div>
                  </>
                )}

                {profile.resume_url && (
                  <>
                    <h5>Resume</h5>
                    <div className="candidate-resume-actions">
                      <a className="candidate-resume-preview" href={profile.resume_url} target="_blank" rel="noreferrer">
                        <span className="la la-file-pdf-o"></span>
                        <div>
                          <strong>{profile.resume_original_name ?? "Candidate Resume"}</strong>
                          <p>Uploaded resume available for employer review.</p>
                        </div>
                      </a>
                      {profile.resume_download_url && (
                        <a className="theme-btn btn-style-three candidate-resume-download" href={profile.resume_download_url} target="_blank" rel="noreferrer">
                          Download
                        </a>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </CandidatePageShell>
  );
};

export default CandidateViewProfile;
